/* ============================================================================
   FRONT LINE DEV - DO NOT EDIT
   This file powers Infinit's custom Quiz and Custom Formula functionality and
   is maintained exclusively by Front Line Dev. Editing it may break the
   formula builder, quiz, saved formulas, or related features. Please contact
   Front Line Dev for any changes.
   ============================================================================ */

/**
 * Formula URL Hash Utility
 *
 * Encodes and decodes custom formula configurations to/from URL hash as base64-encoded JSON.
 * Enables sharing and bookmarking of custom protein and hydration formula configurations.
 *
 * Hash format: #eyJ0IjoicCIsImYiOiJDaG9jb2xhdGUiLCJmaSI6OTAsLi4ufQ==
 *
 * JSON key mapping (for compact encoding):
 *   t   = type (p=protein, h=hydration)
 *   f   = flavor name
 *   fi  = flavor_slider (0-100)
 *   cc  = calorie_carbs_slider (0/50/100, protein only)
 *   cal = calories_slider (0-100, hydration only)
 *   carb= carbs_slider (0-100, hydration only)
 *   el  = electrolytes_slider (0-100, hydration only)
 *   aa  = aminoacids_slider (0-100, hydration only)
 *   caf = caffeine_slider (0-100, hydration only)
 *   ps  = protein_slider (0-100, hydration only)
 *   pt  = protein_type name
 *   b   = boosts (array of IDs)
 *   pk  = package_size (numeric)
 */

window.FormulaUrlHash = (function() {
  'use strict';

  // Key mappings for compact JSON keys
  const KEY_MAP = {
    type: 't',
    flavor: 'f',
    flavor_slider: 'fi',
    calorie_carbs_slider: 'cc',
    calories_slider: 'cal',
    carbs_slider: 'carb',
    electrolytes_slider: 'el',
    aminoacids_slider: 'aa',
    caffeine_slider: 'caf',
    protein_slider: 'ps',
    protein_type: 'pt',
    boosts: 'b',
    boost_labels: 'bl',
    package_size: 'pk'
  };

  // Reverse mapping
  const REVERSE_KEY_MAP = {};
  for (const [full, short] of Object.entries(KEY_MAP)) {
    REVERSE_KEY_MAP[short] = full;
  }

  // Debounce timer reference
  let hashUpdateTimer = null;
  const DEBOUNCE_MS = 500;

  /**
   * Normalize a string for matching (lowercase, remove special chars, collapse spaces)
   * @param {string} str
   * @returns {string}
   */
  function normalizeForMatch(str) {
    return (str || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // remove special chars except spaces
      .replace(/\s+/g, ' ')        // collapse multiple spaces
      .trim();
  }

  /**
   * Encode a config object to a base64-encoded JSON hash string
   * @param {Object} config - Configuration object
   * @returns {string} Base64-encoded hash string (without leading #)
   */
  function encode(config) {
    const compactObj = {};

    for (const [fullKey, value] of Object.entries(config)) {
      if (value === undefined || value === null || value === '') continue;

      const shortKey = KEY_MAP[fullKey] || fullKey;

      // Handle boosts array - only include if not empty
      if (fullKey === 'boosts' && Array.isArray(value)) {
        if (value.length > 0) {
          compactObj[shortKey] = value;
        }
      } else {
        compactObj[shortKey] = value;
      }
    }

    // Return empty if nothing to encode
    if (Object.keys(compactObj).length === 0) {
      return '';
    }

    try {
      const jsonStr = JSON.stringify(compactObj);
      // Use btoa with URI encoding for Unicode safety
      return btoa(unescape(encodeURIComponent(jsonStr)));
    } catch (e) {
      console.warn('[formula-url-hash] Failed to encode config:', e);
      return '';
    }
  }

  /**
   * Decode a base64-encoded JSON hash string to a config object
   * @param {string} hash - Hash string (with or without leading #)
   * @returns {Object} Configuration object
   */
  function decode(hash) {
    // Remove leading # if present
    const cleanHash = hash.replace(/^#/, '');
    if (!cleanHash) return {};

    try {
      // Decode base64 with URI decoding for Unicode safety
      const jsonStr = decodeURIComponent(escape(atob(cleanHash)));
      const compactObj = JSON.parse(jsonStr);

      // Convert short keys back to full keys
      const config = {};
      for (const [shortKey, value] of Object.entries(compactObj)) {
        const fullKey = REVERSE_KEY_MAP[shortKey] || shortKey;
        config[fullKey] = value;
      }

      return config;
    } catch (e) {
      console.warn('[formula-url-hash] Failed to parse hash:', e);
      return {};
    }
  }

  /**
   * Get the current form state for a given formula type
   * @param {string} type - 'protein' or 'hydration'
   * @returns {Object} Current config from form
   */
  function getConfigFromForm(type) {
    const config = {
      type: type === 'protein' ? 'p' : 'h'
    };

    // Get flavor
    const flavorRadio = document.querySelector('input[name="properties[Flavor]"]:checked');
    if (flavorRadio && flavorRadio.value) {
      config.flavor = flavorRadio.value;
    }

    // Get flavor intensity slider
    const flavorSlider = document.getElementById('flavor_slider');
    if (flavorSlider) {
      config.flavor_slider = parseInt(flavorSlider.value, 10);
    }

    // Type-specific sliders
    if (type === 'protein') {
      // Calorie & Carbs slider (protein only)
      const ccSlider = document.getElementById('calorie_carbs_slider');
      if (ccSlider) {
        config.calorie_carbs_slider = parseInt(ccSlider.value, 10);
      }
    } else {
      // Hydration-specific sliders
      const hydrationSliders = [
        'calories_slider',
        'carbs_slider',
        'electrolytes_slider',
        'aminoacids_slider',
        'caffeine_slider',
        'protein_slider'
      ];

      for (const sliderId of hydrationSliders) {
        const slider = document.getElementById(sliderId);
        if (slider) {
          config[sliderId] = parseInt(slider.value, 10);
        }
      }
    }

    // Get protein type
    const proteinRadio = document.querySelector('input[name="properties[Protein Type]"]:checked');
    if (proteinRadio && proteinRadio.value) {
      config.protein_type = proteinRadio.value;
    }

    // Get boosts
    const boostCheckboxes = document.querySelectorAll('input[name="boosts[]"]:checked');
    if (boostCheckboxes.length > 0) {
      config.boosts = Array.from(boostCheckboxes).map(cb => cb.value);
    }

    // Get package size - try multiple sources
    let packageSize = null;

    // Try variant picker radio buttons first
    const variantRadio = document.querySelector('variant-picker input.js-option[type="radio"]:checked');
    if (variantRadio) {
      const match = variantRadio.value.match(/(\d+)/);
      if (match) packageSize = parseInt(match[1], 10);
    }

    // Try select elements
    if (!packageSize) {
      const pkgSelect = document.querySelector('select[name="properties[Package Size]"]') ||
                        document.getElementById('ch-package-size');
      if (pkgSelect) {
        const match = pkgSelect.value.match(/(\d+)/);
        if (match) packageSize = parseInt(match[1], 10);
      }
    }

    // Try input elements
    if (!packageSize) {
      const pkgInput = document.querySelector('input[name="properties[Package Size]"]:checked');
      if (pkgInput) {
        const match = pkgInput.value.match(/(\d+)/);
        if (match) packageSize = parseInt(match[1], 10);
      }
    }

    if (packageSize) {
      config.package_size = packageSize;
    }

    return config;
  }

  /**
   * Apply a config object to the form
   * @param {Object} config - Configuration object
   * @param {string} pageType - 'protein' or 'hydration' (current page type)
   */
  function applyConfigToForm(config, pageType) {
    // Verify type matches page
    const configType = config.type === 'p' ? 'protein' : (config.type === 'h' ? 'hydration' : null);
    if (configType && configType !== pageType) {
      console.log('[formula-url-hash] Type mismatch, ignoring hash');
      return;
    }

    // 1. Set slider values first (may trigger visibility logic)
    applySliders(config, pageType);

    // 2. Set flavor selection (case-insensitive, normalized match)
    if (config.flavor) {
      const flavorRadios = document.querySelectorAll('input[name="properties[Flavor]"]');
      const targetFlavor = normalizeForMatch(config.flavor);
      let matched = false;

      // First try exact match (normalized)
      for (const radio of flavorRadios) {
        if (normalizeForMatch(radio.value) === targetFlavor) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          matched = true;
          break;
        }
      }

      // If no exact match, try partial match (contains)
      if (!matched) {
        for (const radio of flavorRadios) {
          const radioVal = normalizeForMatch(radio.value);
          if (radioVal.includes(targetFlavor) || targetFlavor.includes(radioVal)) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
            break;
          }
        }
      }
    }

    // 3. Set protein type selection (case-insensitive, normalized match)
    if (config.protein_type) {
      const proteinRadios = document.querySelectorAll('input[name="properties[Protein Type]"]');
      const targetProtein = normalizeForMatch(config.protein_type);
      let matched = false;

      // First try exact match (normalized)
      for (const radio of proteinRadios) {
        if (normalizeForMatch(radio.value) === targetProtein) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          matched = true;
          break;
        }
      }

      // If no exact match, try partial match (contains)
      if (!matched) {
        for (const radio of proteinRadios) {
          const radioVal = normalizeForMatch(radio.value);
          if (radioVal.includes(targetProtein) || targetProtein.includes(radioVal)) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
            break;
          }
        }
      }
    }

    // 4. Set package size
    if (config.package_size) {
      applyPackageSize(config.package_size);
    }

    // 5. Wait for boosts to load, then apply (labels let us re-select boosts
    //    whose stored IDs are stale by matching on label)
    if (config.boosts && config.boosts.length > 0) {
      applyBoostsWhenReady(config.boosts, undefined, config.boost_labels);
    }

    // 6. Dispatch update events
    const eventName = pageType === 'protein' ? 'custom-protein:request-update' : 'custom-hydration:request-update';
    document.dispatchEvent(new CustomEvent(eventName));

    // 7. Trigger resize to recalculate slider thumb positions
    setTimeout(function() {
      window.dispatchEvent(new Event('resize'));
    }, 100);
  }

  /**
   * Apply slider values from config
   * @param {Object} config
   * @param {string} pageType
   */
  function applySliders(config, pageType) {
    // Common slider
    if (config.flavor_slider !== undefined) {
      setSliderValue('flavor_slider', config.flavor_slider);
    }

    if (pageType === 'protein') {
      // Protein-specific
      if (config.calorie_carbs_slider !== undefined) {
        setSliderValue('calorie_carbs_slider', config.calorie_carbs_slider);
      }
    } else {
      // Hydration-specific
      const hydrationSliders = [
        'calories_slider',
        'carbs_slider',
        'electrolytes_slider',
        'aminoacids_slider',
        'caffeine_slider',
        'protein_slider'
      ];

      for (const sliderId of hydrationSliders) {
        if (config[sliderId] !== undefined) {
          setSliderValue(sliderId, config[sliderId]);
        }
      }
    }
  }

  /**
   * Set a slider value with proper clamping and event dispatch
   * @param {string} sliderId
   * @param {number} value
   */
  function setSliderValue(sliderId, value) {
    const slider = document.getElementById(sliderId);
    if (!slider) return;

    const min = parseInt(slider.min, 10) || 0;
    const max = parseInt(slider.max, 10) || 100;
    const clamped = Math.min(Math.max(value, min), max);

    slider.value = clamped;
    slider.dispatchEvent(new Event('input', { bubbles: true }));
    slider.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Apply package size selection
   * @param {number} size
   */
  function applyPackageSize(size) {
    const sizeStr = String(size);

    // Try variant picker radio buttons
    const variantRadios = document.querySelectorAll('variant-picker input.js-option[type="radio"]');
    for (const radio of variantRadios) {
      const match = radio.value.match(/(\d+)/);
      if (match && match[1] === sizeStr) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
    }

    // Try select elements
    const pkgSelects = [
      document.querySelector('select[name="properties[Package Size]"]'),
      document.getElementById('ch-package-size')
    ];

    for (const select of pkgSelects) {
      if (!select) continue;
      for (const option of select.options) {
        const match = option.value.match(/(\d+)/);
        if (match && match[1] === sizeStr) {
          select.value = option.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }
    }

    // Try input elements
    const pkgInputs = document.querySelectorAll('input[name="properties[Package Size]"]');
    for (const input of pkgInputs) {
      const match = input.value.match(/(\d+)/);
      if (match && match[1] === sizeStr) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
    }
  }

  /**
   * Wait for boosts to load, then apply selections
   * @param {Array<string>} boostIds - Array of boost IDs to select
   * @param {number} maxAttempts - Maximum poll attempts
   */
  function applyBoostsWhenReady(boostIds, maxAttempts, boostLabels) {
    if (maxAttempts === undefined) maxAttempts = 20;
    let attempts = 0;

    // Normalize IDs to strings so comparisons work regardless of whether
    // the source stored them as numbers (quiz engine JSON) or strings.
    const normalizedIds = boostIds.map(function(id) { return String(id); });

    // Label fallback: Magento-migrated saved formulas can carry stale boost IDs
    // that no longer match the current CSV catalog. When an ID misses, match the
    // checkbox by its label instead (stripped to alphanumeric so "Beta-Alanine",
    // "Beta Alanine" and "betaalanine" all compare equal).
    function stripAll(s) { return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]/g, ''); }
    const wantedLabels = (Array.isArray(boostLabels) ? boostLabels : []).map(stripAll).filter(Boolean);

    function tryApply() {
      attempts++;

      const boostCheckboxes = document.querySelectorAll('input[name="boosts[]"]');

      // Check if boosts have loaded (more than the "Loading" placeholder)
      if (boostCheckboxes.length === 0 && attempts < maxAttempts) {
        setTimeout(tryApply, 150);
        return;
      }

      // Apply selections
      let anyChecked = false;
      for (const checkbox of boostCheckboxes) {
        const idMatch = normalizedIds.includes(checkbox.value);
        const lblMatch = !idMatch && wantedLabels.length > 0 &&
          wantedLabels.includes(stripAll(checkbox.dataset && checkbox.dataset.label));
        if (idMatch || lblMatch) {
          checkbox.checked = true;
          anyChecked = true;
        }
      }

      // Dispatch change event to sync line item properties
      if (anyChecked && boostCheckboxes.length > 0) {
        boostCheckboxes[0].dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    tryApply();
  }

  /**
   * Schedule a debounced hash update
   * @param {string} type - 'protein' or 'hydration'
   */
  function scheduleHashUpdate(type) {
    if (hashUpdateTimer) {
      clearTimeout(hashUpdateTimer);
    }

    hashUpdateTimer = setTimeout(function() {
      const config = getConfigFromForm(type);
      const hash = encode(config);

      if (hash) {
        history.replaceState(null, '', '#' + hash);
      } else {
        // Remove hash if empty
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }, DEBOUNCE_MS);
  }

  /**
   * Initialize hash handling for a page
   * @param {string} pageType - 'protein' or 'hydration'
   */
  function init(pageType) {
    // Don't auto-init on quiz results page (multiple product sections)
    if (document.querySelector('[data-formula-role="the-plan"]')) return;

    // Check for existing hash on page load
    const hash = window.location.hash;
    if (hash && hash.length > 1) {
      const config = decode(hash);

      // Only apply if config has meaningful data
      if (Object.keys(config).length > 1 || (Object.keys(config).length === 1 && !config.type)) {
        // Delay application to ensure DOM is ready
        setTimeout(function() {
          applyConfigToForm(config, pageType);
        }, 100);
      }
    }

    // Set up live hash updates
    setupLiveHashUpdates(pageType);
  }

  /**
   * Set up event listeners for live hash updates
   * @param {string} pageType
   */
  function setupLiveHashUpdates(pageType) {
    // Slider inputs
    const sliderClass = pageType === 'protein' ? '.protein-range' : '.hydration-range';
    document.querySelectorAll(sliderClass).forEach(function(slider) {
      slider.addEventListener('change', function() {
        scheduleHashUpdate(pageType);
      });
    });

    // Also listen by ID for sliders that might not have the class
    const sliderIds = ['flavor_slider'];
    if (pageType === 'protein') {
      sliderIds.push('calorie_carbs_slider');
    } else {
      sliderIds.push('calories_slider', 'carbs_slider', 'electrolytes_slider',
                     'aminoacids_slider', 'caffeine_slider', 'protein_slider');
    }

    for (const id of sliderIds) {
      const slider = document.getElementById(id);
      if (slider && !slider.dataset.hashListenerAdded) {
        slider.dataset.hashListenerAdded = '1';
        slider.addEventListener('change', function() {
          scheduleHashUpdate(pageType);
        });
      }
    }

    // Flavor radio buttons - use event delegation
    document.addEventListener('change', function(e) {
      if (e.target.matches && e.target.matches('input[name="properties[Flavor]"]')) {
        scheduleHashUpdate(pageType);
      }
    });

    // Protein type radio buttons - use event delegation
    document.addEventListener('change', function(e) {
      if (e.target.matches && e.target.matches('input[name="properties[Protein Type]"]')) {
        scheduleHashUpdate(pageType);
      }
    });

    // Boost checkboxes - use event delegation (boosts load async)
    document.addEventListener('change', function(e) {
      if (e.target.matches && e.target.matches('input[name="boosts[]"]')) {
        scheduleHashUpdate(pageType);
      }
    });

    // Package size - use event delegation
    document.addEventListener('change', function(e) {
      if (e.target.matches) {
        if (e.target.matches('select[name="properties[Package Size]"]') ||
            e.target.matches('input[name="properties[Package Size]"]') ||
            e.target.matches('variant-picker input.js-option[type="radio"]') ||
            e.target.id === 'ch-package-size') {
          scheduleHashUpdate(pageType);
        }
      }
    });
  }

  // Public API
  return {
    encode: encode,
    decode: decode,
    getConfigFromForm: getConfigFromForm,
    applyConfigToForm: applyConfigToForm,
    applyBoostsWhenReady: applyBoostsWhenReady,
    scheduleHashUpdate: scheduleHashUpdate,
    init: init
  };
})();
